import React from 'react';
import { Box, Text } from 'ink';
import type { AgentNodeStatus } from '../../types';
import type { SubSessionView } from '../sub_session';
import { useTheme, type ThemeTokens } from '../theme';
import { ControlsHint } from './ControlsHint';

/** The text roles a sub-agent status can map to; keeps `tokens.text` indexable. */
type StatusRole = Extract<keyof ThemeTokens['text'], 'success' | 'danger' | 'warning' | 'subtle'>;

const STATUS_STYLES: Record<AgentNodeStatus, { label: string; role: StatusRole }> = {
  running: { label: 'running', role: 'warning' },
  waiting: { label: 'waiting for approval', role: 'warning' },
  returned: { label: 'returned', role: 'success' },
  failed: { label: 'failed', role: 'danger' },
  cancelled: { label: 'cancelled', role: 'subtle' },
};

const childrenHint = (childCount: number): string => {
  if (childCount === 0) return '';
  if (childCount === 1) return ' • **Ctrl+T** to enter its sub-session';
  return ' • **Ctrl+T** to pick a sub-session';
};

/** Replaces the prompt input while a sub-agent's log is viewed: breadcrumb + navigation hints. */
export const SubSessionPanel: React.FC<{ subSession: SubSessionView; showEscHint: boolean }> = ({
  subSession,
  showEscHint,
}) => {
  const { tokens } = useTheme();
  const { path, node, parent } = subSession;
  const status = node.status ? STATUS_STYLES[node.status] : undefined;

  return (
    <Box
      flexDirection="column"
      borderStyle="single"
      borderColor={tokens.text.subtle}
      borderLeft={false}
      borderRight={false}
      paddingX={1}
    >
      <Box gap={1}>
        <Text>
          {path.map((ancestor, index) => (
            <React.Fragment key={ancestor.id}>
              {index > 0 && <Text dimColor> › </Text>}
              <Text bold={ancestor.id === node.id}>{ancestor.displayName}</Text>
            </React.Fragment>
          ))}
        </Text>
        {status && <Text color={tokens.text[status.role]}>({status.label})</Text>}
      </Box>
      <ControlsHint>{`**←** back to ${parent.displayName}${childrenHint(node.children.length)}`}</ControlsHint>
      {showEscHint && (
        <Text color={tokens.text.warning}>
          Esc for cancellation only works on the main agent. Press ← to go back.
        </Text>
      )}
    </Box>
  );
};
